import React, { useState, useEffect, useCallback } from 'react';
import { PrimaryButton, SecondaryButton, FormInput } from './ui';
import {
  Stack,
  Switch,
  FormControlLabel,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Add as AddIcon, Kitchen as KitchenIcon } from '@mui/icons-material';
import { getKitchens, createKitchen, getPrinters } from '../api/api';
import type { Kitchen, SpiceLevel } from '../types/menu';
import { SPICE_LEVELS } from '../types/menu';
import { useTheme } from '../context/ThemeContext';
import MenuItemImageUpload from './MenuItemImageUpload';
import { IMAGE_GATEWAY_URL } from '../config/images';
import { ALLERGEN_LABELS, ALLERGEN_FREE_KEY, ALLERGEN_FREE_LABEL } from '../config/allergens';
import { DietaryDot, DIETARY_LABELS } from './DietaryDot';

interface ItemFormData {
  name: string;
  description?: string;
  price: number;
  available: boolean;
  quantity?: number | null;
  kitchenId?: number;
  spiceLevel?: SpiceLevel;
  allowsSpiceCustomization?: boolean;
  imageKey?: string;
  allergens?: string[];
  allergenNote?: string;
  /** Positive "no known allergens" declaration; mutually exclusive with allergens */
  allergenFree?: boolean;
  /** Dietary marker (VEG/NON_VEG/VEGAN); 'NONE' = undeclared */
  dietaryType?: string;
}

interface AddItemFormProps {
  vendorId?: number;
  categoryId?: number;
  onSubmit: (data: ItemFormData) => void;
  onCancel: () => void;
}

const AddItemForm: React.FC<AddItemFormProps> = ({ vendorId, categoryId, onSubmit, onCancel }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    available: true,
    quantity: '',
    freeToppingsCount: '0',
    extraToppingPrice: '0',
    kitchenId: '',
    spiceLevel: 'NONE' as SpiceLevel,
    allowsSpiceCustomization: false,
    allergens: [] as string[],
    allergenNote: '',
    dietaryType: 'NONE'
  });
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  // Controlled so picking the exclusive "No known allergens" option can close
  // the menu immediately (multi-picking allergens keeps it open, as standard).
  const [allergenSelectOpen, setAllergenSelectOpen] = useState(false);

  const handleImageUploaded = useCallback((key: string) => {
    setImageKey(key);
    setImageUrl(`${IMAGE_GATEWAY_URL}/${key}`);
  }, []);

  const handleImageRemoved = useCallback(() => {
    setImageKey(null);
    setImageUrl(null);
  }, []);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Kitchen creation state
  const [kitchenDialogOpen, setKitchenDialogOpen] = useState(false);
  const [printers, setPrinters] = useState<any[]>([]);
  const [kitchenFormData, setKitchenFormData] = useState({
    name: '',
    type: '',
    description: '',
    isActive: true,
    printerId: ''
  });
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchKitchens();
  }, []);

  const fetchKitchens = async () => {
    try {
      setLoading(true);
      const [kitchenData, printerData] = await Promise.all([
        getKitchens(),
        getPrinters()
      ]);
      setKitchens(kitchenData);
      setPrinters(printerData || []);
      
      // Set default kitchen if available
      const defaultKitchen = kitchenData.find((k: Kitchen) => k.isDefault);
      if (defaultKitchen) {
        setFormData(prev => ({ ...prev, kitchenId: defaultKitchen.id.toString() }));
      }
    } catch (err) {
      setError('Failed to load kitchens');
      console.error('Error fetching kitchens:', err);
    } finally {
      setLoading(false);
    }
  };

  // Kitchen creation functions
  const handleOpenKitchenDialog = () => {
    setKitchenFormData({
      name: '',
      type: '',
      description: '',
      isActive: true,
      printerId: ''
    });
    setKitchenDialogOpen(true);
  };

  const handleCloseKitchenDialog = () => {
    setKitchenDialogOpen(false);
    setKitchenFormData({
      name: '',
      type: '',
      description: '',
      isActive: true,
      printerId: ''
    });
  };

  const handleCreateKitchen = async () => {
    try {
      const kitchenData = {
        name: kitchenFormData.name.trim(),
        type: kitchenFormData.type.trim() || 'GENERAL',
        description: kitchenFormData.description.trim(),
        isActive: kitchenFormData.isActive,
        printerId: kitchenFormData.printerId ? parseInt(kitchenFormData.printerId) : undefined
      };
      
      const newKitchen = await createKitchen(kitchenData);
      setSuccess(`Kitchen "${kitchenData.name}" created successfully!`);
      
      // Refresh kitchens list and auto-select the new kitchen
      await fetchKitchens();
      setFormData(prev => ({ ...prev, kitchenId: newKitchen.id.toString() }));
      
      handleCloseKitchenDialog();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create kitchen');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const itemData: ItemFormData = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      available: formData.available,
      quantity: formData.quantity ? parseInt(formData.quantity) : null,
      kitchenId: formData.kitchenId ? parseInt(formData.kitchenId) : undefined,
      spiceLevel: formData.spiceLevel || 'NONE',
      allowsSpiceCustomization: formData.allowsSpiceCustomization,
      imageKey: imageKey || undefined,
      // The "No known allergens" option is a UI sentinel — map it to the
      // allergenFree boolean and send an empty allergen list.
      allergens: formData.allergens.includes(ALLERGEN_FREE_KEY) ? [] : formData.allergens,
      allergenFree: formData.allergens.includes(ALLERGEN_FREE_KEY),
      allergenNote: formData.allergenNote.trim() || undefined,
      dietaryType: formData.dietaryType,
    };
    onSubmit(itemData);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {vendorId && categoryId && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Item Image
              </Typography>
              <MenuItemImageUpload
                vendorId={vendorId}
                type="items"
                entityId={categoryId}
                imageUrl={imageUrl}
                imageKey={imageKey}
                onImageUploaded={handleImageUploaded}
                onImageRemoved={handleImageRemoved}
              />
            </Box>
          )}

          <FormInput
            label="Item Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
        
        <FormInput
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={3}
          fullWidth
        />
        
        <FormInput
          label="Price"
          type="number"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          required
          fullWidth
          inputProps={{ step: 0.01, min: 0 }}
        />
        
        <FormInput
          label="Stock (Coming Soon)"
          type="number"
          value={formData.quantity}
          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
          fullWidth
          disabled
          inputProps={{ min: 0 }}
          helperText="Stock management feature coming soon."
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
              cursor: 'not-allowed'
            }
          }}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={formData.available}
              onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
            />
          }
          label="Available"
        />
        
        <Box display="flex" gap={2}>
          <FormInput
            label="Free Toppings Count"
            type="number"
            value={formData.freeToppingsCount}
            onChange={(e) => setFormData({ ...formData, freeToppingsCount: e.target.value })}
            fullWidth
            inputProps={{ min: 0 }}
            helperText="Number of toppings included at no extra cost"
          />
          
          <FormInput
            label="Extra Topping Price"
            type="number"
            value={formData.extraToppingPrice}
            onChange={(e) => setFormData({ ...formData, extraToppingPrice: e.target.value })}
            fullWidth
            inputProps={{ step: 0.01, min: 0 }}
            helperText="Price for each additional topping"
          />
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}
        
        <FormControl fullWidth>
          <InputLabel id="kitchen-select-label">Kitchen</InputLabel>
          <Select
            labelId="kitchen-select-label"
            value={formData.kitchenId}
            label="Kitchen"
            onChange={(e) => {
              if (e.target.value === 'CREATE_NEW') {
                handleOpenKitchenDialog();
              } else {
                setFormData({ ...formData, kitchenId: e.target.value });
              }
            }}
            disabled={loading}
          >
            <MenuItem value="">
              <em>None (Default Kitchen)</em>
            </MenuItem>
            {kitchens.map((kitchen) => (
              <MenuItem key={kitchen.id} value={kitchen.id.toString()}>
                {kitchen.name} {kitchen.isDefault && '(Default)'} - {kitchen.kitchenType}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem value="CREATE_NEW" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
              <AddIcon sx={{ mr: 1, fontSize: 20 }} />
              Create New Kitchen
            </MenuItem>
          </Select>
        </FormControl>

        {/* Dietary marker — the classic green/red square-dot + vegan leaf */}
        <FormControl fullWidth>
          <InputLabel id="dietary-type-select-label">Dietary Type</InputLabel>
          <Select
            labelId="dietary-type-select-label"
            value={formData.dietaryType}
            label="Dietary Type"
            onChange={(e) => setFormData({ ...formData, dietaryType: e.target.value })}
          >
            <MenuItem value="NONE">
              <em>Not declared</em>
            </MenuItem>
            {(['VEG', 'NON_VEG', 'VEGAN'] as const).map((t) => (
              <MenuItem key={t} value={t}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <DietaryDot type={t} size="md" />
                  {DIETARY_LABELS[t]}
                </span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="spice-level-select-label">Default Spice Level</InputLabel>
          <Select
            labelId="spice-level-select-label"
            value={formData.spiceLevel}
            label="Default Spice Level"
            onChange={(e) => setFormData({ ...formData, spiceLevel: e.target.value as SpiceLevel })}
          >
            {Object.entries(SPICE_LEVELS).map(([key, config]) => (
              <MenuItem key={key} value={key}>
                {config.icon && <span style={{ marginRight: 8 }}>{config.icon}</span>}
                {config.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Allergen declaration (UK plan Phase 4) — same Select pattern as spice level */}
        <FormControl fullWidth>
          <InputLabel id="allergen-select-label">Allergens (select all that apply)</InputLabel>
          <Select
            labelId="allergen-select-label"
            multiple
            value={formData.allergens}
            label="Allergens (select all that apply)"
            open={allergenSelectOpen}
            onOpen={() => setAllergenSelectOpen(true)}
            onClose={() => setAllergenSelectOpen(false)}
            onChange={(e) => {
              const v = e.target.value;
              const next = typeof v === 'string' ? v.split(',') : v;
              // "No known allergens" is mutually exclusive with everything:
              // newly ticking it clears the allergens; ticking an allergen
              // while it is set clears it. It also CLOSES the menu — nothing
              // further can be selected, so keeping it open is dead air.
              // Multi-picking real allergens keeps the menu open (standard
              // multi-select behaviour; checkboxes signal the toggling).
              const hadNone = formData.allergens.includes(ALLERGEN_FREE_KEY);
              const hasNone = next.includes(ALLERGEN_FREE_KEY);
              const resolved = hasNone && !hadNone
                ? [ALLERGEN_FREE_KEY]
                : next.filter((a) => a !== ALLERGEN_FREE_KEY);
              if (hasNone && !hadNone) setAllergenSelectOpen(false);
              setFormData({ ...formData, allergens: resolved });
            }}
            renderValue={(selected) => (selected as string[])
              .map((a) => (a === ALLERGEN_FREE_KEY ? `✓ ${ALLERGEN_FREE_LABEL}` : ALLERGEN_LABELS[a] || a))
              .join(', ')}
          >
            <MenuItem value={ALLERGEN_FREE_KEY} sx={{ color: 'success.main', fontWeight: 600 }}>
              ✓ {ALLERGEN_FREE_LABEL}
            </MenuItem>
            <Divider />
            {Object.entries(ALLERGEN_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={formData.allergens.includes(key)} size="small" sx={{ py: 0, pl: 0 }} />
                <ListItemText primary={label} />
              </MenuItem>
            ))}
            {/* Sticky footer: explicit dismissal for the stay-open multi-select
                (same "Done" pattern as the guest allergen filter panel) */}
            <Box
              sx={{
                position: 'sticky', bottom: 0, zIndex: 1,
                bgcolor: 'background.paper',
                borderTop: '1px solid', borderColor: 'divider',
                px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {formData.allergens.includes(ALLERGEN_FREE_KEY)
                  ? ALLERGEN_FREE_LABEL
                  : `${formData.allergens.length} selected`}
              </Typography>
              <PrimaryButton size="small" onClick={() => setAllergenSelectOpen(false)}>
                Done
              </PrimaryButton>
            </Box>
          </Select>
        </FormControl>

        <FormInput
          label="Allergen Note (optional)"
          value={formData.allergenNote}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, allergenNote: e.target.value })}
          placeholder="e.g. May contain traces of nuts"
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.allowsSpiceCustomization}
              onChange={(e) => setFormData({ ...formData, allowsSpiceCustomization: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--status-bad)',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--status-bad)',
                }
              }}
            />
          }
          label="Allow Customer Spice Customization"
        />

        <Box display="flex" gap={2} justifyContent="flex-end">
          <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
          <PrimaryButton type="submit" disabled={!formData.name || !formData.price}>Add Item</PrimaryButton>
        </Box>
      </Stack>
    </form>

    {/* Kitchen Creation Dialog */}
    <Dialog 
      open={kitchenDialogOpen} 
      onClose={handleCloseKitchenDialog} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <KitchenIcon />
          <Typography variant="h6">Create New Kitchen</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormInput
                fullWidth
                label="Kitchen Name"
                value={kitchenFormData.name}
                onChange={(e) => setKitchenFormData({ ...kitchenFormData, name: e.target.value })}
                required
                placeholder="e.g., Bar Kitchen, Main Kitchen"
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                fullWidth
                label="Kitchen Type/Area (Optional)"
                value={kitchenFormData.type}
                onChange={(e) => setKitchenFormData({ ...kitchenFormData, type: e.target.value })}
                placeholder="e.g., Bar Area, Pool Area, Main Dining"
                helperText="Optional: Specify the type or area this kitchen serves"
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                fullWidth
                label="Description"
                value={kitchenFormData.description}
                onChange={(e) => setKitchenFormData({ ...kitchenFormData, description: e.target.value })}
                multiline
                rows={2}
                placeholder="Brief description of this kitchen's purpose"
              />
            </Grid>
            {printers.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Printer (Optional)</InputLabel>
                  <Select
                    value={kitchenFormData.printerId}
                    onChange={(e) => setKitchenFormData({ ...kitchenFormData, printerId: e.target.value })}
                    label="Printer (Optional)"
                  >
                    <MenuItem value="">
                      <em>No Printer</em>
                    </MenuItem>
                    {printers.map((printer) => (
                      <MenuItem key={printer.id} value={printer.id.toString()}>
                        {printer.name} ({printer.ipAddress})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={kitchenFormData.isActive}
                    onChange={(e) => setKitchenFormData({ ...kitchenFormData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleCloseKitchenDialog}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleCreateKitchen} disabled={!kitchenFormData.name.trim()}>Create Kitchen</PrimaryButton>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default AddItemForm;